"use client";

import { useEffect, useRef, useState, useCallback } from "react";

// =====================================================
// Testimonials Data
// =====================================================
const testimonials = [
  {
    quote:
      "Herr S. konnte mir bei einem Problem im Zusammenhang mit FTX super weiterhelfen. Auf Freundlichkeit, Kompetenz und Pünktlichkeit wird großen Wert gelegt. Vielen Dank und gerne weiter so!",
    name: "Sven G.",
    context: "FTX-Betroffener",
  },
  {
    quote:
      "Die FTX-Videos haben mir geholfen, meine verlorenen Gelder tatsächlich zurückzubekommen. Ohne diese kostenlose Hilfe wäre ich komplett aufgeschmissen gewesen.",
    name: "Hunderte Discord Mitglieder & YouTube Zuschauer",
    context: "FTX-Betroffene, 2025",
  },
  {
    quote:
      "Endlich jemand, der Bitcoin verständlich erklärt, ohne dabei zu verkaufen. Die wöchentlichen Analysen sind Gold wert.",
    name: "Sarah",
    context: "Blog-Leserin seit 2022",
  },
  {
    quote:
      "Nach der persönlichen Beratung fühle ich mich endlich sicher mit meinen Bitcoin. Die Einrichtung der Hardware-Wallet war einfacher als gedacht.",
    name: "Michael",
    context: "Beratungsklient seit 2024",
  },
  {
    quote:
      "Der persönliche Service und die einhergehende Unterstützung bei der Einrichtung meiner Cold-Wallet war professionell. Fragen wurden umfassend beantwortet und die Antworten waren logisch und nachvollziehbar gehalten.",
    name: "Jürgen H.",
    context: "Beratungsklient seit 2023",
  },
];

// =====================================================
// Testimonial Card Component
// =====================================================
interface TestimonialCardProps {
  quote: string;
  name: string;
  context: string;
}

function TestimonialCard({ quote, name, context }: TestimonialCardProps) {
  return (
    <figure className="rounded-2xl p-8 text-sm/6" style={{ backgroundColor: "rgba(255,255,255,0.025)" }}>
      <blockquote>
        <p
          style={{
            fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
            fontSize: "clamp(0.85rem, 1.5vw, 1rem)",
            lineHeight: "170%",
            color: "#b7ab98",
            fontStyle: "italic",
          }}
        >
          "{quote}"
        </p>
      </blockquote>
      <figcaption className="mt-6 flex items-center gap-x-4">
        {/* Avatar mit Initialen */}
        <div
          className="flex items-center justify-center rounded-full"
          style={{
            width: "40px",
            height: "40px",
            backgroundColor: "rgba(247, 147, 26, 0.15)",
            border: "1px solid rgba(247, 147, 26, 0.3)",
          }}
        >
          <span
            style={{
              fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "#f7931a",
            }}
          >
            {name.charAt(0)}
          </span>
        </div>
        <div>
          <div
            style={{
              fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "#b7ab98",
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: "0.8rem",
              color: "hsla(37, 18%, 66%, 0.6)",
            }}
          >
            {context}
          </div>
        </div>
      </figcaption>
    </figure>
  );
}

// =====================================================
// Main Section Component
// =====================================================
export function VertrauenSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [showContent, setShowContent] = useState(false);
  const rafRef = useRef<number | undefined>(undefined);

  const checkVisible = useCallback(() => {
    if (!sectionRef.current || showContent) return;

    const rect = sectionRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    if (rect.top < windowHeight * 0.7) {
      setShowContent(true);
    }
  }, [showContent]);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(checkVisible);
    };

    checkVisible();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [checkVisible]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: "#0d0d0d" }}
    >
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32">
        {/* Header */}
        <h2
          className="text-center select-none mb-16"
          style={{
            fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 700,
            fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
            letterSpacing: "-0.03em",
            lineHeight: "110%",
            color: "#b7ab98",
            opacity: showContent ? 1 : 0,
            transform: showContent ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s",
          }}
        >
          Was andere über uns sagen.
        </h2>

        {/* Testimonials Grid - Masonry Layout */}
        <div
          className="-mt-8 sm:-mx-4 sm:columns-2 lg:columns-3"
          style={{
            opacity: showContent ? 1 : 0,
            transition: "opacity 1s ease-out 0.3s",
          }}
        >
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.name}
              className="pt-8 sm:inline-block sm:w-full sm:px-4"
            >
              <TestimonialCard
                quote={testimonial.quote}
                name={testimonial.name}
                context={testimonial.context}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
